import { Editor, loader, Monaco } from "@monaco-editor/react";
import { getEditorTheme, loadMonaco } from "components/Form/components/CodeEditor/monacoSetup";
import type { editor } from "monaco-editor";
import { useEffect, useRef, useState } from "react";
import style from "./EditableMarkdown.module.scss";

const MIN_HEIGHT = 90;
const MAX_HEIGHT = 400;

// Module-level latch: once Monaco has been loaded/configured in this session, it
// stays available. Subsequent MarkdownEditor mounts (every time the user enters
// edit on a description) can then skip the async load gate and the height-reserved
// placeholder entirely, so entering edit doesn't flicker (view -> placeholder ->
// editor). Only the very first edit in a session pays the one-frame placeholder.
// (Margot, 2026-09-04: "it still flickers ... rendering a placeholder seems clumsy".)
let monacoPreloaded = false;

type MarkdownEditorProps = {
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    autoFocus?: boolean;
    readOnly?: boolean;
    // When set (the boxed record-description variant), the editor STARTS at this
    // pixel height so entering edit on an already-tall expanded description does not
    // flash at MIN_HEIGHT first — then it auto-grows/-shrinks with the content like
    // the auto-sizing field it replaced. null/undefined = start at MIN_HEIGHT (the
    // schema-form markdown-attribute path, unchanged).
    initialHeight?: number | null;
    // Upper bound the editor grows to before it scrolls internally. The boxed
    // description passes the same 65vh cap its view box uses, so the edit box grows
    // as tall as the expanded view before a scrollbar appears (Margot, 2026-09-07:
    // "switch back to the growing height until max height while editing, and show a
    // scrollbar once there is content outside the viewport"). null/undefined = the
    // default MAX_HEIGHT (schema-form path, unchanged).
    maxHeight?: number | null;
    // When true (the boxed record-description variant), a mouse-wheel scroll is
    // kept inside the editor even at the top/bottom boundary, so scrolling while
    // editing does not chain out to the page and compress the sticky header
    // (Margot, 2026-09-09: "in edit mode inside the description, I want to scroll
    // only there"). Default false keeps the schema-form path releasing the wheel
    // at the boundary.
    consumeMouseWheel?: boolean;
};

/**
 * The plain-text ("edit") view of a MARKDOWN form field, rendered with the same
 * Monaco editor the Console uses everywhere else (flow/script/text fields) —
 * configured for the `markdown` language so headings, emphasis, links, code
 * fences etc. are syntax-highlighted while typing.
 *
 * It reuses the shared `monacoSetup` bootstrap (loadMonaco / getEditorTheme) so
 * it participates in the same coordinated Monaco load profile as CodeEditor,
 * rather than pulling in a second Monaco instance. Markdown fields do not need
 * breakpoints, linting or the current-line indicator, so this is a thin editor
 * without CodeEditor's execution-oriented machinery.
 *
 * Height auto-grows with the content (clamped between MIN_HEIGHT and
 * MAX_HEIGHT), mirroring the auto-sizing textarea it replaces; beyond the
 * maximum the editor scrolls internally.
 */
export const MarkdownEditor = ({
    value,
    onChange,
    onBlur,
    autoFocus,
    readOnly,
    initialHeight,
    maxHeight,
    consumeMouseWheel = false,
}: MarkdownEditorProps) => {
    const effectiveMax =
        typeof maxHeight === "number" && maxHeight > MIN_HEIGHT ? maxHeight : MAX_HEIGHT;
    const seedHeight =
        typeof initialHeight === "number" && initialHeight > 0
            ? Math.min(effectiveMax, Math.max(MIN_HEIGHT, initialHeight))
            : MIN_HEIGHT;
    // Start ready (skip the placeholder) when Monaco was already loaded earlier
    // this session — this is what removes the enter-edit flicker on every edit
    // after the first.
    const [editorReady, setEditorReady] = useState(monacoPreloaded);
    const [height, setHeight] = useState(seedHeight);
    const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
    // Guards the deferred (rAF) height sync below against firing after the editor has
    // been unmounted (leaving edit mode disposes the Monaco instance).
    const mountedRef = useRef(true);
    useEffect(() => () => {
        mountedRef.current = false;
    }, []);
    const language = "markdown";

    useEffect(() => {
        // Already loaded once — nothing to await, no placeholder frame, no flicker.
        if (monacoPreloaded) {
            return undefined;
        }
        let cancelled = false;
        setEditorReady(false);

        const initialize = async () => {
            try {
                const monaco: Monaco = await loadMonaco(language);
                loader.config({ monaco });
                monacoPreloaded = true;
                if (!cancelled) {
                    setEditorReady(true);
                }
            } catch (error) {
                console.error("Failed to initialize markdown editor", error);
            }
        };

        void initialize();

        return () => {
            cancelled = true;
        };
    }, []);

    // Auto-grow to the content height, clamped between MIN_HEIGHT and the effective
    // maximum; beyond the maximum Monaco scrolls internally (its own single vertical
    // scrollbar). Growing-then-scrolling mirrors the auto-sizing text field the
    // description used to be (Margot, 2026-09-07).
    const syncHeight = (editorInstance: editor.IStandaloneCodeEditor) => {
        // Read the content height on the NEXT frame, after Monaco's word-wrap relayout
        // has settled. onDidContentSizeChange fires mid-reflow, so reading
        // synchronously could catch an intermediate height — the box was sized to that
        // interim value and then corrected, a visible "jump" on each newline while it
        // auto-grows (Margot, 2026-09-08). Deferring to the settled value, and only
        // applying it when it actually changed, keeps the growth smooth.
        requestAnimationFrame(() => {
            if (!mountedRef.current) {
                return;
            }
            const contentHeight = editorInstance.getContentHeight();
            const next = Math.min(effectiveMax, Math.max(MIN_HEIGHT, contentHeight));
            setHeight((prev) => (prev !== next ? next : prev));
        });
    };

    // Until Monaco has (re)initialised, render the current text at a reserved
    // height rather than nothing. Returning `null` here made the edit box collapse
    // to zero for a frame on entering edit — shoving the record action buttons up
    // under the title and back (the "blink" Margot reported, 2026-09-02). The
    // placeholder keeps the box the same size so the swap to Monaco is seamless.
    if (!editorReady) {
        return (
            <div
                className={style.editorLoadingPlaceholder}
                style={{ height, minHeight: height, maxHeight: height }}
            >
                {value}
            </div>
        );
    }

    return (
        <Editor
            height={height}
            language={language}
            theme={getEditorTheme(language)}
            value={value}
            options={{
                minimap: { enabled: false },
                readOnly: readOnly ?? false,
                // Prose-oriented: wrap long lines, drop the line-number/glyph
                // gutters so the editor reads like an enriched text field and
                // fills the full available width.
                wordWrap: "on",
                lineNumbers: "off",
                glyphMargin: false,
                folding: false,
                lineDecorationsWidth: 0,
                lineNumbersMinChars: 0,
                scrollBeyondLastLine: false,
                automaticLayout: true,
                // A description is free prose, not code: Monaco's autocomplete /
                // IntelliSense (word-based suggestions, the suggestion widget) irritate
                // more than they help here and the popup gets clipped by the box edge
                // (Margot, 2026-09-08: "the auto-complete is cut off ... I find [it]
                // highly irritating ... maybe simply remove it"). Turn the whole
                // suggestion machinery off so it stays a plain writing surface.
                quickSuggestions: false,
                suggestOnTriggerCharacters: false,
                wordBasedSuggestions: false,
                parameterHints: { enabled: false },
                tabCompletion: "off",
                acceptSuggestionOnEnter: "off",
                cursorBlinking: "smooth",
                overviewRulerLanes: 0,
                renderLineHighlight: "none",
                scrollbar: {
                    // In the boxed description, keep the wheel inside the editor at
                    // the scroll boundary so editing does not chain-scroll the page /
                    // compress the header (Margot, 2026-09-09, #5). Elsewhere the
                    // wheel is released at the boundary so a short markdown attribute
                    // never traps the page scroll.
                    alwaysConsumeMouseWheel: consumeMouseWheel,
                    // No scroll-shadow: Monaco paints a grey top-edge shadow when
                    // the content is scrolled, which read as a stray "grey line"
                    // over the box's top border/spacing (Margot, 2026-09-04).
                    useShadows: false,
                    // wordWrap is on, so there is never horizontal content to
                    // scroll; suppress the horizontal bar so it can't flash a
                    // second scrollbar. The vertical bar (Monaco's own) is the
                    // single scrollbar; the box itself does not scroll (SCSS).
                    horizontal: "hidden",
                    vertical: "auto",
                    // A clearly visible vertical bar (matching the app's 11px
                    // scrollbars) once the content grows past the height cap
                    // (Margot, 2026-09-07: "there is no scroll bar in edit mode").
                    verticalScrollbarSize: 11,
                },
            }}
            onChange={(next) => onChange(next ?? "")}
            onMount={(editorInstance) => {
                editorRef.current = editorInstance;
                syncHeight(editorInstance);
                editorInstance.onDidContentSizeChange(() => syncHeight(editorInstance));
                if (onBlur) {
                    editorInstance.onDidBlurEditorText(() => onBlur());
                }
                if (autoFocus) {
                    editorInstance.focus();
                }
            }}
        />
    );
};
